import marketCarts from 'Assets/js/Vuex/Store/stores/marketCarts';
import b2bCartHelpers from 'Assets/js/components/b2bCartHelpers';
import events from 'Assets/js/components/events';
import cartContent from 'Templates/content/cart.json';
import { isEmpty } from 'lodash-es';

function syncB2bContext(context, market, cartData) {
  context.commit('addB2bMarketCheckoutStatus', {
    market,
    checkoutStatus: cartData.status
  });
  context.commit('setB2bMarketCartOrderId', {
    market,
    orderId: cartData.id
  });
  context.commit('setB2bMarketCartArbitraryCartId', {
    market,
    arbitraryCartId: cartData.name
  });
  context.commit('addB2bMarketCheckoutUrl', {
    market,
    checkoutUrl: cartData.invoiceUrl
  });

  context.commit('updateB2BMarketCartTotal', {
    market,
    total: cartData.totalPrice
  });
}

const actions = {
  addDiscountToCart(context, payload) {
    if (payload !== '') {
      context.getters.userInstance.checkDiscount(payload, (response) => {
        if (!response.data.data.checkDiscountCode.valid) {
          window.dispatchEvent(
            new CustomEvent('error-toggled', {
              detail: {
                subtitle: 'Error',
                title: 'Error adding discount code',
                content: 'This is not a valid discount code.'
              }
            })
          );
          // Trigger event
          events.cartDiscountCode('');
        } else {
          context.commit('setDiscountCode', payload);
          // Trigger event
          events.cartDiscountCode(payload);
        }
      });
    } else {
      context.commit('removeDiscountCode');
      // Trigger event
      events.cartDiscountCode('');
    }
  },
  async getShippingCost(context, payload) {
    const { market, productId, carttotal, recipientId } = payload;
    let { weight } = payload;

    // Round weight to 1 digit!
    // This is necessary for two reasons:
    // 1. when adding 3 product with 0.7 kg each
    // - Javascript: 0.7 * 3 ==> 2.0999999999999996
    // 2. when a product has a weight with 2 digits, e.g. Powers Notebook = 0.21 kg
    // - Powers Notebook (each 0.21 kg) x 5 quantity = 1.05 kg
    // - For UK, weight 1.05 kg wouldn't be picked by any B2B rate, because they are 0-1 kg and 1.1-2 kg
    weight = Number(weight.toFixed(1));

    const theMarket = market.toUpperCase();

    const { shipping } = window.global_theme_object;
    const marketShippings = shipping[theMarket];

    if (!marketShippings) {
      // eslint-disable-next-line no-console
      console.warn(`No shipping available for market ${theMarket}`, {
        theMarket,
        productId,
        weight,
        shipping
      });

      return 0.0;
    }

    // Find recipient
    const recipient = (context.getters.b2bUserList || []).find(
      (user) => user.id === recipientId
    );
    if (!recipient) {
      // eslint-disable-next-line no-console
      console.warn(
        `No shipping available for non-existing recipient ${recipientId}`,
        {
          theMarket,
          productId,
          recipientId
        }
      );

      return 0.0;
    }

    /**
     * Check market
     * @param {Object} marketShipping
     * @param {Boolean} checkProduct If true, force product check
     * @returns {null}
     */
    const checkMarket = (marketShipping, checkProduct = true) => {
      let localPrice = null;

      // Check market postcodes
      const postcodes = marketShipping?.postcodes || [];

      const recipientInPostcode = postcodes.length
        ? !!postcodes.filter((postcode) => {
            // BT => include only postcodes beginning with BT (Northern Ireland)
            if (postcode.match(/^!/)) {
              // Exclude
              return !new RegExp(`^${postcode.substring(1)}`, 'gi').exec(
                recipient.address.postcode
              );
            }
            // Include
            return new RegExp(`^${postcode}`, 'gi').exec(
              recipient.address.postcode
            );
          }).length
        : true;

      if (recipientInPostcode !== true) {
        // Recipient postcode is not available in the market shipping zone,
        // e.g. Northern Ireland postcode (starting with BT) in UK shipping zone (not starting with BT)

        // Remove console.warn(): it is not a failure so it may be misleading
        // eslint-disable-next-line no-console
        // console.warn(
        //   'Cannot find shipping price: recipient postcode is not available in the market shipping zone',
        //   {
        //     postcodes,
        //     address: recipient.address
        //   }
        // );
        return null;
      }

      // Check if market has provinces
      // If no provinces (e.g. Singapore market), skip this check
      // IMPORTANT: Actually, all markets are not using provinces anymore but postcodes
      const provinces = marketShipping?.provinces?.provinces || [];
      const recipientInProvince = provinces.length
        ? !!provinces.filter(
            (province) => province.code === recipient.address.provinceCode
          ).length
        : true;
      if (recipientInProvince !== true) {
        // Recipient province is not available in the market shipping zone,
        // e.g. Northern Ireland address in UK shipping zone (without NI)
        // eslint-disable-next-line no-console
        console.warn(
          'Cannot find shipping price: recipient province is not available in the market shipping zone',
          {
            provinces,
            address: recipient.address
          }
        );

        return null;
      }

      // Find product
      const found = checkProduct
        ? (
            marketShipping.shipping_delivery_profile?.products?.products || []
          ).find((product) => product.id === productId)
        : true;

      if (found) {
        localPrice = (
          marketShipping.price_based_shipping_rates.rates ?? []
        ).find((item) => {
          if (
            item.min_order_price <= carttotal &&
            (item.max_order_price >= carttotal || item.max_order_price === 0)
          ) {
            return item;
          }
          return null;
        });

        if (!localPrice) {
          localPrice = marketShipping.weight_based_shipping_rates.rates.find(
            (item) => {
              if (item.weight_low <= weight && item.weight_high >= weight) {
                return item;
              }
              return null;
            }
          );
        }
      }

      return localPrice;
    };

    let price = null;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < marketShippings.length; i++) {
      const marketShipping = marketShippings[i];
      // Get price by market with product check
      price = checkMarket(marketShipping);

      if (price !== null) {
        break;
      }
    }

    // Try fallback market, if product is not found because it's a new one
    if (price === null) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < marketShippings.length; i++) {
        const marketShipping = marketShippings[i];
        // Get price by market with product check
        price = checkMarket(marketShipping, false);

        if (price !== null) {
          break;
        }
      }
    }

    const ret = price?.price ?? null;

    if (ret === null) {
      // eslint-disable-next-line no-console
      console.warn('No shipping found', {
        theMarket,
        productId,
        recipientId,
        weight,
        shipping,
        marketShippings,
        price
      });
    }

    return ret;
  },
  resetB2bCarts(context) {
    const ossUser = localStorage.getItem('ossUser');
    if (ossUser) {
      const storedState = JSON.parse(ossUser);
      // // eslint-disable-next-line no-console
      // console.log(
      //   'storedState.b2bCarts length',
      //   storedState.b2bCarts ? storedState.b2bCarts.length : 0
      // );
      if (storedState && storedState.b2bCarts) {
        context.commit('resetB2bCarts', storedState.b2bCarts);
      }
    }
  },
  async checkB2bCartTotals(context) {
    const b2bCarts = Object.entries(context.getters.b2bCarts);
    // eslint-disable-next-line no-restricted-syntax
    for (const cart of b2bCarts) {
      const market = cart[0];
      const theCart = cart[1];
      if (Object.values(theCart.contactItems).length === 0) {
        context.commit('setB2bCartTotals', {
          market,
          totals: { total: 0, tax: 0, shipping: 0 }
        });
      }
    }
  },
  async dumpB2bCarts(context, payload = { reload: false }) {
    const { reload } = payload;
    context.commit('resetCart');
    context.commit('resetB2bCarts', {
      global: {
        initiated: false,
        attachUserOpenInCart: false,
        giftMessage: '',
        discountCodes: [],
        products: []
      },
      markets: [],
      carts: [],
      marketCarts
    });

    document.cookie =
      'STYXKEY_user_cart=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

    if (reload === true) {
      window.dispatchEvent(
        new CustomEvent('cart-changed', {
          detail: {
            cart: context.getters.cartInstance.getCart()
          }
        })
      );

      await new Promise((resolve) => setTimeout(resolve, 500)); // Adjust the delay time as needed

      window.location.reload();
    }
  },

  async processCarts(context, payload = { market: null, noRepeat: false }) {
    const b2bHelper = b2bCartHelpers.getInstance();

    let marketsToProcess = [];

    if (payload.market === null) {
      marketsToProcess = [
        ...new Set(
          context.getters.b2bCartLocations.map((market) => market.toLowerCase())
        )
      ];
    } else {
      marketsToProcess = [payload.market.toLowerCase()];
    }

    await Promise.all(
      marketsToProcess.map(async (market) => {
        const currentCart = context.getters.b2bCarts[market];
        const lineItems = new Set();

        Object.values(currentCart.contactItems).forEach((contactItem) => {
          Object.values(contactItem.products).forEach((product) => {
            lineItems.add(product.productId);
          });
        });
        const uniqueLineItems = [...lineItems];

        if (uniqueLineItems.length === 0) {
          await b2bHelper.deleteOrderById(currentCart.order.id, () => {
            context.commit('resetMarketCartOrder', market);
            context.commit('purgeMarket', market);
          });
        }

        context.commit('setMarketCartProductQuantity', {
          market,
          productQuantity: uniqueLineItems.length
        });

        if (payload.noRepeat) {
          return;
        }

        await b2bHelper
          .createDraftCartAsync({
            market,
            company: context.getters.b2bCompany,
            allLineItems: currentCart.contactItems
          })
          .then((cartData) => {
            syncB2bContext(context, market, cartData);
          });
      })
    );
  },
  setB2bApiCartHash(context, payload) {
    context.commit('setB2bApiCartHash', payload);
  },
  setGlobalGiftMessage(context, payload) {
    context.commit('setGlobalGiftMessage', payload);
    context.dispatch('processCarts').then(() => {});
  },
  async addRecipientsWithProductToCart(context, payload) {
    if (payload.ids.length === 0) {
      return;
    }

    // Build list of users
    payload.users = [];
    payload.ids.forEach((id) => {
      if (id) {
        payload.users.push(
          context.getters.recipients.find((recipient) => recipient.id === id)
        );
      }
    });

    // Validate users
    payload.users.forEach((user) => {
      if (!user.market) {
        window.dispatchEvent(
          new CustomEvent('error-toggled', {
            detail: {
              subtitle: 'Error',
              title: 'Please add an address for this user',
              content: `A recipient order requires the recipient to have a delivery address. Please add an address for ${user.name} and try again.`
            }
          })
        );
      }
      context.commit('addCartLocation', user.market);
    });

    payload.company = context.getters.b2bCompany;

    await new Promise((resolve) => {
      context.getters.cartInstance.addRecipientsWithProductToCart(
        payload.ids,
        payload.variantId,
        payload.quantity,
        payload.engraving ?? false,
        payload.giftBag ?? false,
        payload.linkedVariantId ?? '',
        (response) => {
          const responseData = response?.data;
          const cartLinesAdd = responseData?.data?.cartLinesAdd;
          const graphQlErrors = responseData?.errors || [];
          const userErrors = cartLinesAdd?.userErrors || [];
          const firstErrorMessage =
            userErrors[0]?.message ||
            graphQlErrors[0]?.message ||
            'An unexpected error occurred. Please try again.';

          if (graphQlErrors.length > 0 || userErrors.length > 0) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: 'Error',
                  title: 'An error occurred adding this item to the cart',
                  content: firstErrorMessage
                }
              })
            );
            resolve(false);
            return;
          }

          if (!cartLinesAdd?.cart?.lines?.edges) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: 'Error',
                  title: 'An error occurred adding this item to the cart',
                  content: firstErrorMessage
                }
              })
            );
            resolve(false);
            return;
          }

          const allLines = cartLinesAdd.cart.lines.edges.filter(
            (line) => line.node.quantity > 0
          );

          payload.users.forEach((user) => {
            user.lineitem = allLines.find((item) => {
              if (!item.node.attributes || item.node.attributes.length === 0) {
                return false;
              }
              const attribute = item.node.attributes.find(
                (attr) =>
                  attr.key === cartContent.lineItemsAttributes.b2b_recipient
              );
              return (
                item.node.merchandise.id === payload.variantId &&
                attribute &&
                attribute.value === user.id
              );
            })?.node;

            if (user.lineitem && user.lineitem.quantity !== 0) {
              user.lineitem.quantity = payload.quantity;
            }
            user.variantId = payload.variantId;
            user.company = payload.company;

            if (!user.lineitem) {
              // eslint-disable-next-line no-console
              console.warn('No lineitem found for user:', user);
            } else if (user.lineitem.quantity > 0) {
              context.dispatch('addingContactItem', user);
            } else {
              const productName = user.lineitem.merchandise.product.title;
              window.dispatchEvent(
                new CustomEvent('error-toggled', {
                  detail: {
                    subtitle: 'Error',
                    title: 'An error occurred adding this item to the cart',
                    content: `Could not add ${productName} to ${user.name} as stock levels are too low.`
                  }
                })
              );
            }
          });
          context.dispatch('resetB2bCarts');
          window.dispatchEvent(
            new CustomEvent('cart-changed', {
              detail: { cart: context.getters.cartInstance.getCart() }
            })
          );
          resolve(true);
        }
      );
    }).then(async (success = true) => {
      if (success === false) {
        return;
      }

      context.commit('appendB2bGlobalRecipients', payload);

      const b2bHelper = b2bCartHelpers.getInstance();

      // Retrieve markets to process
      const marketsToProcess = [
        ...new Set(
          payload.users.map((user) => {
            if (!user || !user.market) {
              return;
            }
            return user.market.toLowerCase();
          })
        )
      ];

      try {
        const cartPromises = marketsToProcess.map(async (market) => {
          const currentCart = context.getters.b2bCarts[market];
          if (!currentCart) {
            return;
          }

          const cartData = await b2bHelper.createDraftCartAsync({
            market,
            company: payload.company,
            allLineItems: currentCart.contactItems
          });
          syncB2bContext(context, market, cartData);
        });

        await Promise.all(cartPromises);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error in markets to process', e);
      }

      context
        .dispatch('processCarts', { market: null, noRepeat: true })
        .then(() => {});
    });
  },
  async updateLineItemWithBottledBy(context, payload) {
    // Build list of users
    payload.users = [];

    payload.company = context.getters.b2bCompany;

    Object.keys(context.getters.b2bCarts).forEach((marketCode) => {
      const market = context.getters.b2bCarts[marketCode];
      if (market.initiated) {
        Object.values(market.contactItems).forEach((contact) => {
          Object.values(contact.products).forEach((product) => {
            if (product.cartLineId === payload.cartLineId) {
              const user = context.getters.recipients.find(
                (recipient) => recipient.id === contact.id
              );
              user.variantId = product.variantId;
              user.company = payload.company;

              // Add or update personalisation label
              if (isEmpty(payload.message)) {
                // Remove bottled by
                product.attributes = product.attributes.filter(
                  (attribute) =>
                    attribute.key !==
                      cartContent.lineItemsAttributes.bottled_by &&
                    attribute.key !== cartContent.lineItemsAttributes.for &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.is_engrave_product &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.b2b_engrave_variant &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.gift_bag &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.b2b_gift_bag_variant &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.b2b_is_linked_product &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.b2b_linked_variant_id
                );
              } else if (payload.bottledByOnly === true) {
                // Bottled by without for
                // Find line items
                const bottledByOnlyAttributes = product.attributes.filter(
                  (attribute) =>
                    attribute.key === cartContent.lineItemsAttributes.bottled_by
                );
                if (bottledByOnlyAttributes.length) {
                  bottledByOnlyAttributes[0].value = payload.message.bottledBy;
                } else {
                  product.attributes.push({
                    key: cartContent.lineItemsAttributes.bottled_by,
                    value: payload.message.bottledBy
                  });
                }
              } else {
                // Bottled by with for
                // Find line items
                const bottledByAttributes = product.attributes.filter(
                  (attribute) =>
                    attribute.key === cartContent.lineItemsAttributes.bottled_by
                );
                const forAttributes = product.attributes.filter(
                  (attribute) =>
                    attribute.key === cartContent.lineItemsAttributes.for
                );

                if (bottledByAttributes.length) {
                  bottledByAttributes[0].value = payload.message.bottledBy;
                } else {
                  product.attributes.push({
                    key: cartContent.lineItemsAttributes.bottled_by,
                    value: payload.message.bottledBy
                  });
                }

                if (forAttributes.length) {
                  forAttributes[0].value = payload.message.for;
                } else {
                  product.attributes.push({
                    key: cartContent.lineItemsAttributes.for,
                    value: payload.message.for
                  });
                }
              }

              // Prepare line item
              user.lineitem = {
                merchandise: {
                  product: {
                    id: product.productId,
                    title: product.title
                  },
                  thumbnail: {
                    url: product.image
                  },
                  weight: product.weight
                },
                attributes: product.attributes,
                id: product.cartLineId,
                quantity: product.quantity
              };

              // Bottled by
              if (payload.bottledByOnly === true) {
                user.lineitem.merchandise.product.bottled_by_only =
                  product.bottled_by_only;
              } else {
                user.lineitem.merchandise.product.bottled_by_for =
                  product.bottled_by_for;
              }

              // Add gift bag
              // @todo Is that used?
              if (payload.giftBag) {
                product.attributes.push({
                  key: cartContent.lineItemsAttributes.gift_bag,
                  value: payload.engraving.productId
                });
                product.attributes.push({
                  key: cartContent.lineItemsAttributes.b2b_gift_bag_variant,
                  value: payload.engraving.variantId
                });
                product.attributes.push({
                  key: cartContent.lineItemsAttributes.b2b_linked_variant_id,
                  value: payload.engraving.linkedVariantId
                });
              }

              // Remove contact and add them back with personalisation
              context.commit('removingContactItem', product.cartLineId);
              context.dispatch('addingContactItem', user);

              payload.users.push(user);
            }
          });
        });
      }
    });

    // Process users
    const b2bHelper = b2bCartHelpers.getInstance();

    // Retrieve markets to process
    const marketsToProcess = [
      ...new Set(payload.users.map((user) => user.market.toLowerCase()))
    ];

    await Promise.all(
      marketsToProcess.map(async (market) => {
        const currentCart = context.getters.b2bCarts[market];
        const cartData = await b2bHelper.createDraftCartAsync({
          market,
          company: payload.company,
          allLineItems: currentCart.contactItems
        });

        // Sync context
        syncB2bContext(context, market, cartData);
      })
    );
  },
  async addingContactItem(context, payload) {
    context.commit('addingContactItem', payload);
  },
  async updateProductInGlobalCart(context, payload) {
    const foundItem = Object.values(context.getters.b2bGlobalProducts).find(
      (item) => item.products.variantId === payload.variantId
    );

    if (!foundItem) {
      // eslint-disable-next-line no-console
      console.error(
        `B2b/Orders.updateProductInGlobalCart failed, no products found for variant ID: ${payload.variantId}`
      );

      return;
    }

    const formattedPayload = {
      variantId: payload.variantId,
      productId: foundItem?.products?.productId,
      quantity: payload.quantity
    };

    context.commit('updateProductInGlobalCart', formattedPayload);

    const activeMarkets = context.getters.b2bCartLocations;
    activeMarkets.forEach((market) => {
      context.commit('updateProductInMarketCart', {
        market,
        ...formattedPayload
      });
    });

    context.dispatch('processCarts').then(() => {});
  },
  async handleAttachUserOpenInCart(context, payload) {
    context.commit('setAttachUserOpenInCart', payload);
  },
  async addDispatchProductForTotal(context, payload) {
    context.commit('setB2bUserCartTotal', payload);
  },
  async updateLineItemWithPersonalisation(context, payload) {
    payload.users = [];
    payload.company = context.getters.b2bCompany;

    Object.keys(context.getters.b2bCarts).forEach((marketCode) => {
      const market = context.getters.b2bCarts[marketCode];
      if (market.initiated) {
        Object.values(market.contactItems).forEach((contact) => {
          Object.values(contact.products).forEach((product) => {
            if (product.cartLineId === payload.cartLineId) {
              const user = context.getters.recipients.find(
                (recipient) => recipient.id === contact.id
              );
              user.variantId = product.variantId;
              user.company = payload.company;

              const personalisationAttributes = product.attributes.filter(
                (attribute) =>
                  attribute.key ===
                  cartContent.lineItemsAttributes.personalisation
              );

              if (payload.label === '') {
                payload.engraving = '';
                product.attributes = product.attributes.filter(
                  (attribute) =>
                    attribute.key !==
                      cartContent.lineItemsAttributes.personalisation &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.is_engrave_product &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.b2b_engrave_variant &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.gift_bag &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.b2b_gift_bag_variant &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.b2b_is_linked_product &&
                    attribute.key !==
                      cartContent.lineItemsAttributes.b2b_linked_variant_id
                );
              } else if (personalisationAttributes.length) {
                personalisationAttributes[0].value = payload.label;
              } else {
                product.attributes.push({
                  key: cartContent.lineItemsAttributes.personalisation,
                  value: payload.label
                });
              }

              user.lineitem = {
                merchandise: {
                  product: {
                    id: product.productId,
                    title: product.title,
                    requires_personalisation: product.requires_personalisation,
                    personalisation_limit: product.personalisation_limit,
                    personalisation_product: product.personalisation_product
                  },
                  thumbnail: {
                    url: product.image
                  },
                  weight: product.weight
                },
                attributes: product.attributes,
                id: product.cartLineId,
                quantity: product.quantity
              };

              if (payload.engraving) {
                product.attributes.push({
                  key: cartContent.lineItemsAttributes.is_engrave_product,
                  value: payload.engraving.productId
                });
                product.attributes.push({
                  key: cartContent.lineItemsAttributes.b2b_engrave_variant,
                  value: payload.engraving.variantId
                });
                product.attributes.push({
                  key: cartContent.lineItemsAttributes.b2b_linked_variant_id,
                  value: payload.engraving.linkedVariantId
                });
              }

              if (payload.giftBag) {
                product.attributes.push({
                  key: cartContent.lineItemsAttributes.gift_bag,
                  value: payload.engraving.productId
                });
                product.attributes.push({
                  key: cartContent.lineItemsAttributes.b2b_gift_bag_variant,
                  value: payload.engraving.variantId
                });
                product.attributes.push({
                  key: cartContent.lineItemsAttributes.b2b_linked_variant_id,
                  value: payload.engraving.linkedVariantId
                });
              }

              // // eslint-disable-next-line no-console
              // console.log('CartOps.updateLineItemWithPersonalisation', {
              //   payload,
              //   product,
              //   user
              // });

              context.commit('removingContactItem', product.cartLineId);
              context.dispatch('addingContactItem', user);

              payload.users.push(user);
            }
          });
        });
      }
    });

    const b2bHelper = b2bCartHelpers.getInstance();
    const marketsToProcess = [
      ...new Set(payload.users.map((user) => user.market.toLowerCase()))
    ];

    await Promise.all(
      marketsToProcess.map(async (market) => {
        const currentCart = context.getters.b2bCarts[market];
        const cartData = await b2bHelper.createDraftCartAsync({
          market,
          company: payload.company,
          allLineItems: currentCart.contactItems
        });

        syncB2bContext(context, market, cartData);
      })
    );
  }
};

export default actions;
